import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  AlertTitle,
  Collapse,
  TablePagination,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';

const DataPreviewModal = ({
  open,
  onClose,
  onBack,
  parsedData,
  onConfirmUpload,
  onDownloadCSV
}) => {

  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showValidation, setShowValidation] = useState(false);


  // Early return if modal is not open
  if (!open) {
    return null;
  }


  // Early return if no parsedData or if data is invalid
  if (!parsedData || typeof parsedData !== 'object') {
    return null;
  }


  // Safely extract data with comprehensive defaults
  const {
    success = false,
    data = [],
    headers = [],
    originalFormat = 'Unknown',
    fileName = 'Unknown File',
    rowCount = 0,
    validation = {},
    error = null
  } = parsedData;

  
  // Ensure all arrays are actually arrays
  const safeData = Array.isArray(data) ? data : [];
  const safeHeaders = Array.isArray(headers) ? headers : [];
  
  // Ensure validation has proper structure
  const safeValidation = {
    isValid: validation?.isValid || true,
    errors: Array.isArray(validation?.errors) ? validation.errors : [],
    warnings: Array.isArray(validation?.warnings) ? validation.warnings : []
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getFileTypeChip = (format) => {
    const colorMap = {
      'csv': 'success',
      'acc': 'info',
      'rlc': 'warning',
      'rld': 'warning',
      'rps': 'secondary',
      'sec': 'primary',
      'tcf': 'success',
      'trn': 'info',
      'uln': 'default',
      'ult': 'default',
      'upn': 'default',
      'upt': 'default',
      'json': 'secondary',
      'xml': 'primary',
      'txt': 'default'
    };
    
    return (
      <Chip
        label={format.toUpperCase()}
        color={colorMap[format] || 'default'}
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  const renderValidationSummary = () => {
    if (!safeValidation) return null;

    const { errors, warnings } = safeValidation;
    const hasIssues = errors.length > 0 || warnings.length > 0;

    if (!hasIssues) {
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Validation Passed</AlertTitle>
          All safeData appears to be valid and ready for upload.
        </Alert>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        {errors && errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <AlertTitle>Validation Errors ({errors.length})</AlertTitle>
            <Collapse in={showValidation}>
              <Box sx={{ mt: 1 }}>
                {errors.slice(0, 5).map((error, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    • {error}
                  </Typography>
                ))}
                {errors.length > 5 && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    ... and {errors.length - 5} more errors
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Alert>
        )}
        
        {warnings && warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <AlertTitle>Validation Warnings ({warnings.length})</AlertTitle>
            <Collapse in={showValidation}>
              <Box sx={{ mt: 1 }}>
                {warnings.slice(0, 5)?.map((warning, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    • {warning}
                  </Typography>
                ))}
                {warnings.length > 5 && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    ... and {warnings.length - 5} more warnings
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Alert>
        )}
        
        <Button
          size="small"
          onClick={() => setShowValidation(!showValidation)}
          startIcon={showValidation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none' }}
        >
          {showValidation ? 'Hide Details' : 'Show Details'}
        </Button>
      </Box>
    );
  };

  if (!success) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="safeData-preview-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        <Box sx={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          position: 'relative',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          padding: '24px'
        }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {onBack && (
                <IconButton onClick={onBack} sx={{ p: 0.5 }}>
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                File Parse Error
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ p: 1 }}>
              <CloseIcon sx={{ fontSize: 24, color: '#666' }} />
            </IconButton>
          </Box>

          {/* Error Content */}
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Failed to Parse File</AlertTitle>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>File:</strong> {fileName}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Error:</strong> {error}
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please check your file format and try again. Supported formats include:
             ACC, RLC and other delimited text files.
          </Typography>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {onBack && (
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{ textTransform: 'none' }}
              >
                Try Again
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="safeData-preview-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 0 24px',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {onBack && (
              <IconButton onClick={onBack} sx={{ p: 0.5 }}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', mb: 0.5 }}>
                Data Preview
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {fileName}
                </Typography>
                {getFileTypeChip(originalFormat)}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <CloseIcon sx={{ fontSize: 24, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Stats and Info */}
        <Box sx={{ padding: '16px 24px', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2">
                <strong>{rowCount}</strong> records parsed
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2">
                <strong>{safeHeaders.length}</strong> columns mapped
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {safeValidation.errors && safeValidation.errors.length > 0 ? (
                <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
              ) : safeValidation.warnings && safeValidation.warnings.length > 0 ? (
                <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              ) : (
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              )}
              <Typography variant="body2">
                Validation {safeValidation.errors && safeValidation.errors.length > 0 ? 'failed' : 'passed'}
              </Typography>
            </Box>
          </Box>
          
          {renderValidationSummary()}
        </Box>

        <Divider />

        {/* Data Table */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {safeHeaders && safeHeaders.length > 0 ? safeHeaders?.map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        borderBottom: '2px solid #e0e0e0'
                      }}
                    >
                      {header}
                    </TableCell>
                  )) : (
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: '14px' }}>
                      No Headers Available
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {safeData && safeData.length > 0 ? safeData
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ?.map((row, index) => {
         
                    return (
                      <TableRow key={index} hover>
                      {safeHeaders && safeHeaders.length > 0 ? safeHeaders?.map((header) => {
                      
                        return (
                          <TableCell
                            key={header}
                            sx={{
                              fontSize: '13px',
                              padding: '8px 16px',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {row[header] || '-'}
                          </TableCell>
                        );
                      }) : (
                        <TableCell sx={{ fontSize: '13px', padding: '8px 16px' }}>
                          No Data Available
                        </TableCell>
                      )}
                    </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell 
                        colSpan={safeHeaders ? safeHeaders.length : 1}
                        sx={{ 
                          textAlign: 'center', 
                          padding: '20px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {/* <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
            <TablePagination
              component="div"
              count={safeData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: 'none' }}
            />
          </Box> */}
        </Box>

        {/* Actions */}
        <Box sx={{
          padding: '24px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          flexShrink: 0
        }}>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => onDownloadCSV && onDownloadCSV(safeData, fileName)}
            sx={{ textTransform: 'none' }}
          >
            Download CSV
          </Button>
          
          {/* <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => onConfirmUpload && onConfirmUpload(parsedData)}
            disabled={validation?.errors?.length > 0}
            sx={{
              textTransform: 'none',
              backgroundColor: validation?.errors?.length > 0 ? '#ccc' : '#1344EB',
              '&:hover': {
                backgroundColor: validation?.errors?.length > 0 ? '#ccc' : '#0D47A1'
              }
            }}
          >
            Confirm Upload
          </Button> */}
        </Box>
      </Box>
    </Modal>
  );
};

export default DataPreviewModal; 